/**
 * Canvas confetti + spark burst (replaces vendor celebration animations). Self-written.
 */
import { useEffect, useRef } from 'react';
import { canvasDpr, isLowPower } from '../lib/power';

const COLORS = ['#FF7A1A', '#FFC23A', '#5AD1FF', '#3DDC84', '#B98BFF', '#FF5C7A', '#FFFBF4'];

/** `colors` = a world's accents (art/worlds theme) so each world celebrates in its own colours. */
export function Confetti({ duration = 1500, count = 120, origin = 'top', colors }: { duration?: number; count?: number; origin?: 'top' | 'center' | 'sides'; colors?: readonly string[] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    // Short-lived full-screen canvas: density ≤ 1.5 (≤ 1 in battery saver), released (0×0) when the burst ends.
    const dpr = isLowPower() ? 1 : Math.min(1.5, canvasDpr());
    const W = (c.width = c.clientWidth * dpr);
    const H = (c.height = c.clientHeight * dpr);
    const parts = Array.from({ length: isLowPower() ? Math.round(count / 2) : count }, (_, i) => {
      const side = origin === 'sides' ? (i % 2 ? 0 : W) : W / 2;
      const x = origin === 'top' ? Math.random() * W : side;
      const y = origin === 'top' ? -20 * dpr : origin === 'center' ? H / 2 : H * 0.8;
      const ang = origin === 'top' ? Math.PI / 2 : origin === 'center' ? Math.random() * Math.PI * 2 : (i % 2 ? -Math.PI / 4 : (-3 * Math.PI) / 4) + (Math.random() - 0.5) * 0.6;
      const sp = (origin === 'top' ? 2 + Math.random() * 3 : 6 + Math.random() * 8) * dpr;
      return {
        x,
        y,
        vx: Math.cos(ang) * sp + (origin === 'top' ? (Math.random() - 0.5) * 2 * dpr : 0),
        vy: Math.sin(ang) * sp,
        r: (4 + Math.random() * 5) * dpr,
        rot: Math.random() * 6,
        vr: (Math.random() - 0.5) * 0.3,
        col: (colors?.length ? [...colors, '#FFFBF4'] : COLORS)[i % (colors?.length ? colors.length + 1 : COLORS.length)]!,
        star: i % 5 === 0,
      };
    });
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const el = t - t0;
      ctx.clearRect(0, 0, W, H);
      const fade = Math.max(0, 1 - Math.max(0, el - duration * 0.6) / (duration * 0.6));
      for (const p of parts) {
        p.vy += 0.12 * dpr;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.col;
        if (p.star) {
          ctx.beginPath();
          for (let k = 0; k < 10; k++) {
            const rr = k % 2 ? p.r * 0.45 : p.r;
            const a = (k * Math.PI) / 5 - Math.PI / 2;
            ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
          }
          ctx.fill();
        } else ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2);
        ctx.restore();
      }
      if (el < duration * 1.2) raf = requestAnimationFrame(tick);
      else (c.width = 0), (c.height = 0);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      c.width = 0;
      c.height = 0;
    };
  }, [duration, count, origin]);
  return <canvas ref={ref} className="confetti" style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 80 }} aria-hidden />;
}
